import axios from 'axios';
import { API_BASE_URL } from '../config/supabase';

export interface APILog {
  id: string;
  timestamp: string;
  gateway: string;
  endpoint: string;
  method: string;
  requestData: any;
  responseData: any;
  status: string | number;
  duration: number;
  error?: string;
  success: boolean;
}

export interface HealthStats {
  total: {
    calls: number;
    success: number;
    failed: number;
    successRate: string;
  };
  lastHour: {
    calls: number;
    success: number;
    failed: number;
    successRate: string;
  };
  last24Hours: {
    calls: number;
    success: number;
    failed: number;
    successRate: string;
  };
  gateways: {
    razorpay: {
      calls: number;
      success: number;
      failed: number;
      avgDuration: number;
    };
    qr_payment: {
      calls: number;
      success: number;
      failed: number;
      avgDuration: number;
    };
  };
}

export interface GatewayConfig {
  razorpay: {
    configured: boolean;
    keyId: string;
    hasSecret: boolean;
  };
  qr_payment: {
    configured: boolean;
    secretId: string;
    hasSecret: boolean;
    baseUrl: string;
  };
}

export interface TestResult {
  success: boolean;
  status: string | number;
  duration: number;
  message: string;
  error?: string;
}

class APIHealthService {
  private baseURL = API_BASE_URL;

  /**
   * Get API health statistics
   */
  async getHealthStats(): Promise<{ success: boolean; data?: HealthStats; error?: string }> {
    try {
      const response = await axios.get(`${this.baseURL}/api-health/stats`, {
        timeout: 10000,
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch health stats:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch health statistics'
      };
    }
  }

  /**
   * Get recent API logs
   */
  async getRecentLogs(limit: number = 50, gateway?: string): Promise<{ success: boolean; data?: { logs: APILog[]; total: number; limit: number; gateway: string }; error?: string }> {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      if (gateway) {
        params.append('gateway', gateway);
      }

      const response = await axios.get(`${this.baseURL}/api-health/logs?${params}`, {
        timeout: 10000,
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch API logs:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch API logs'
      };
    }
  }

  /**
   * Test payment gateway connectivity
   */
  async testGateway(gateway: string): Promise<{ success: boolean; data?: { gateway: string; testResult: TestResult }; error?: string }> {
    try {
      const response = await axios.post(`${this.baseURL}/api-health/test-gateway`, {
        gateway
      }, {
        timeout: 30000,
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to test gateway:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to test gateway'
      };
    }
  }

  /**
   * Test all payment gateways
   */
  async testAllGateways(): Promise<{ success: boolean; data?: { gateways: Record<string, TestResult> }; error?: string }> {
    try {
      const response = await axios.post(`${this.baseURL}/api-health/test-all-gateways`, {}, {
        timeout: 60000,
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to test all gateways:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to test all gateways'
      };
    }
  }

  /**
   * Get detailed log for a specific API call
   */
  async getLogDetails(logId: string): Promise<{ success: boolean; data?: APILog; error?: string }> {
    try {
      const response = await axios.get(`${this.baseURL}/api-health/log/${logId}`, {
        timeout: 10000,
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch log details:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch log details'
      };
    }
  }

  /**
   * Get payment gateway configuration status
   */
  async getGatewayConfig(): Promise<{ success: boolean; data?: GatewayConfig; error?: string }> {
    try {
      const response = await axios.get(`${this.baseURL}/api-health/gateway-config`, {
        timeout: 10000,
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch gateway config:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch gateway configuration'
      };
    }
  }

  /**
   * Clear old logs
   */
  async clearOldLogs(olderThanDays: number = 7): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await axios.delete(`${this.baseURL}/api-health/clear-logs`, {
        data: { olderThanDays },
        timeout: 10000,
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to clear logs:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to clear logs'
      };
    }
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleString();
  }

  /**
   * Get status color for UI
   */
  getStatusColor(status: string | number, success: boolean): string {
    if (success) {
      return 'text-green-500';
    } else if (typeof status === 'number' && status >= 400) {
      return 'text-red-500';
    } else {
      return 'text-yellow-500';
    }
  }

  /**
   * Get status badge color for UI
   */
  getStatusBadgeColor(status: string | number, success: boolean): string {
    if (success) {
      return 'bg-green-100 text-green-800';
    } else if (typeof status === 'number' && status >= 400) {
      return 'bg-red-100 text-red-800';
    } else {
      return 'bg-yellow-100 text-yellow-800';
    }
  }

  /**
   * Get gateway display name
   */
  getGatewayDisplayName(gateway: string): string {
    switch (gateway) {
      case 'razorpay':
        return 'Razorpay';
      case 'qr_payment':
        return 'QR Payment';
      default:
        return gateway;
    }
  }

  /**
   * Get gateway icon
   */
  getGatewayIcon(gateway: string): string {
    switch (gateway) {
      case 'razorpay':
        return '💳';
      case 'qr_payment':
        return '📱';
      default:
        return '🔧';
    }
  }
}

export const apiHealthService = new APIHealthService();
